import React, { useEffect, useState } from "react";
import { TrendingUp, Users, MessageSquare, Star, Sparkles, MapPin, Crown, BadgeCheck, Award, Flame } from "lucide-react";
import api from "@/lib/api";

function MetricCard({ icon: Icon, label, value, sub, color = "#1F2937" }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E7DDCF] p-5" data-testid={`analytics-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2">
        <Icon size={16} style={{ color }} />
        <div className="text-xs uppercase tracking-wider text-[#4B5563]">{label}</div>
      </div>
      <div className="font-heading text-3xl mt-2" style={{ color }}>{value}</div>
      {sub && <div className="text-xs text-[#4B5563] mt-1">{sub}</div>}
    </div>
  );
}

function HorizontalBars({ items, valueKey, labelKey, max, color = "#1F4E3D" }) {
  return (
    <div className="space-y-2.5">
      {items.map((it) => (
        <div key={it[labelKey]} className="text-xs">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[#1F2937] font-medium">{it[labelKey]}</span>
            <span className="text-[#4B5563]">{it[valueKey]}{it.avg_rating ? ` · ★ ${it.avg_rating}` : ""}</span>
          </div>
          <div className="h-2 rounded-full bg-[#F5EFE6] overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${(it[valueKey] / max) * 100}%`, backgroundColor: color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function TimelineChart({ data }) {
  const maxLeads = Math.max(1, ...data.map((d) => d.leads));
  const maxRevs = Math.max(1, ...data.map((d) => d.reviews));
  const max = Math.max(maxLeads, maxRevs, 3);
  return (
    <div data-testid="analytics-timeline-chart" className="rounded-2xl bg-white border border-[#E7DDCF] p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#4B5563]">Last 14 days</div>
          <div className="font-heading text-lg">Activity Timeline</div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#C9A86A]"/> Leads</div>
          <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#1F4E3D]"/> Reviews</div>
        </div>
      </div>
      <div className="flex items-end gap-1 h-32">
        {data.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5 group relative">
            <div className="w-full flex items-end gap-0.5 h-full">
              <div className="flex-1 rounded-t-sm bg-[#C9A86A] transition-all" style={{ height: `${(d.leads / max) * 100}%`, minHeight: d.leads > 0 ? "4px" : "0" }} />
              <div className="flex-1 rounded-t-sm bg-[#1F4E3D] transition-all" style={{ height: `${(d.reviews / max) * 100}%`, minHeight: d.reviews > 0 ? "4px" : "0" }} />
            </div>
            <div className="absolute -top-7 hidden group-hover:block bg-[#111827] text-white text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap">
              {d.date}: {d.leads}L / {d.reviews}R
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-2 text-[10px] text-[#4B5563]">
        <span>{data[0]?.date.slice(5)}</span>
        <span>{data[Math.floor(data.length / 2)]?.date.slice(5)}</span>
        <span>{data[data.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)).catch(() => setA(false)); }, []);

  if (a === null) return <div className="py-12 text-center text-[#4B5563]">Loading analytics...</div>;
  if (a === false) return <div className="py-12 text-center text-[#B42318]">Failed to load analytics</div>;

  const t = a.totals;
  const catMax = Math.max(1, ...a.by_category.map((c) => c.count));
  const locMax = Math.max(1, ...a.by_locality.map((l) => l.count));

  return (
    <div data-testid="admin-analytics-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Insights</div>
        <h2 className="font-heading text-2xl">Dashboard Analytics</h2>
        <p className="text-sm text-[#4B5563] mt-1">Real-time platform insights, performance breakdowns and trends.</p>
      </div>

      {/* Top metric cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <MetricCard icon={Sparkles} label="Total Spas" value={t.spas} sub={`${t.verified} verified`} color="#1F4E3D" />
        <MetricCard icon={MessageSquare} label="Total Leads" value={t.leads} sub="all-time inquiries" color="#C9A86A" />
        <MetricCard icon={Star} label="Reviews" value={t.reviews} sub={`avg ★ ${t.avg_rating}`} color="#C9A86A" />
        <MetricCard icon={Crown} label="Premium / Featured" value={`${t.premium} / ${t.featured}`} sub="paid tier spas" color="#111827" />
      </div>

      {/* Timeline */}
      <div className="mb-6">
        <TimelineChart data={a.timeline_14_days} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        {/* By Category */}
        <div className="rounded-2xl bg-white border border-[#E7DDCF] p-5" data-testid="analytics-by-category">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#4B5563]">Distribution</div>
              <div className="font-heading text-lg">Spas by Category</div>
            </div>
            <Sparkles size={18} className="text-[#C9A86A]" />
          </div>
          <HorizontalBars items={a.by_category.slice(0, 8)} valueKey="count" labelKey="category" max={catMax} color="#1F4E3D" />
        </div>

        {/* By Locality */}
        <div className="rounded-2xl bg-white border border-[#E7DDCF] p-5" data-testid="analytics-by-locality">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#4B5563]">Geography</div>
              <div className="font-heading text-lg">Top Areas by Listings</div>
            </div>
            <MapPin size={18} className="text-[#1F4E3D]" />
          </div>
          <HorizontalBars items={a.by_locality.slice(0, 8)} valueKey="count" labelKey="locality" max={locMax} color="#C9A86A" />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        {/* Top by Rating */}
        <div className="rounded-2xl bg-white border border-[#E7DDCF] p-5" data-testid="analytics-top-rated">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#4B5563]">Excellence</div>
              <div className="font-heading text-lg">Top-Rated Spas</div>
            </div>
            <Award size={18} className="text-[#C9A86A]" />
          </div>
          <ol className="space-y-2">
            {a.top_by_rating.slice(0, 6).map((s, i) => (
              <li key={s.slug} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-[#F5EFE6] text-xs font-medium flex items-center justify-center">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#111827] truncate">{s.spa_name}</div>
                  <div className="text-[11px] text-[#4B5563]">{s.locality} · {s.category}</div>
                </div>
                <div className="flex items-center gap-1 text-sm font-medium">
                  <Star size={12} className="fill-[#C9A86A] text-[#C9A86A]" /> {s.rating?.toFixed(1)}
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Top by Leads */}
        <div className="rounded-2xl bg-white border border-[#E7DDCF] p-5" data-testid="analytics-top-leads">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#4B5563]">Engagement</div>
              <div className="font-heading text-lg">Most Inquired Spas</div>
            </div>
            <Flame size={18} className="text-[#B42318]" />
          </div>
          {a.top_by_leads.length === 0 ? (
            <div className="text-sm text-[#4B5563] py-8 text-center">No inquiries received yet.</div>
          ) : (
            <ol className="space-y-2">
              {a.top_by_leads.slice(0, 6).map((s, i) => (
                <li key={`${s.spa_name}-${i}`} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#F5EFE6] text-xs font-medium flex items-center justify-center">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#111827] truncate">{s.spa_name}</div>
                  </div>
                  <div className="text-sm font-medium text-[#1F4E3D]">{s.leads} lead{s.leads === 1 ? "" : "s"}</div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {/* Rating distribution */}
      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-5" data-testid="analytics-rating-distribution">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#4B5563]">Quality</div>
            <div className="font-heading text-lg">Rating Distribution</div>
          </div>
          <Star size={18} className="fill-[#C9A86A] text-[#C9A86A]" />
        </div>
        <div className="grid grid-cols-4 gap-3">
          {a.rating_distribution.map((b) => (
            <div key={b.bucket} className="rounded-xl bg-[#F5EFE6] p-3 text-center">
              <div className="text-xs text-[#4B5563]">{b.bucket}</div>
              <div className="font-heading text-2xl mt-1 text-[#1F4E3D]">{b.count}</div>
              <div className="text-[10px] text-[#4B5563] mt-1">spas</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
